import type * as THREE from 'three/webgpu';
import type { MeasureClosestPointResult } from '../types/measure-distance-types';
import { computeCenterToTargetReferencePoint } from './measureArcCircleReferencePoints';
import type { MeasureArcCircleMixedContext } from './measureArcCircleMixedReferencePoints';

/**
 * 混合最小距离结果拆分的圆侧/非圆侧参考点。
 */
export interface MeasureMixedMinimumSidePoints {
    /** 最小距离在圆相关几何体上的参考点 A。 */
    circlePoint: THREE.Vector3;
    /** 最小距离在非圆相关几何体上的参考点 B。 */
    nonCirclePoint: THREE.Vector3;
}

/**
 * 从最小距离结果中提取圆侧与非圆侧参考点。
 *
 * @param minimumResult 混合场景最小距离求解结果。
 * @param circleSideIsFirst 圆相关几何体是否为先选实体。
 * @returns 拆分后的两侧参考点。
 */
export function extractMixedMinimumSidePoints(
    minimumResult: MeasureClosestPointResult,
    circleSideIsFirst: boolean,
): MeasureMixedMinimumSidePoints {
    if (circleSideIsFirst) {
        return {
            circlePoint: minimumResult.from.clone(),
            nonCirclePoint: minimumResult.to.clone(),
        };
    }

    return {
        circlePoint: minimumResult.to.clone(),
        nonCirclePoint: minimumResult.from.clone(),
    };
}

/**
 * 基于混合最小距离结果派生圆心到圆心参考点对。
 *
 * 设计说明：
 * 1. 非圆侧参考点 B 与最小距离保持一致（点/线/面/原点/基准面/基准线均适用）；
 * 2. 圆侧参考点取参考点 A 所在圆弧/圆的圆心，由调用方解析后传入。
 *
 * @param minimumResult 混合场景最小距离求解结果。
 * @param context 混合测量上下文。
 * @param circleCenter 参考点 A 对应圆弧/圆的圆心。
 * @returns 圆心到圆心参考点结果。
 */
export function deriveMixedCenterToCenterFromMinimum(
    minimumResult: MeasureClosestPointResult,
    context: MeasureArcCircleMixedContext,
    circleCenter: THREE.Vector3,
): MeasureClosestPointResult {
    const { nonCirclePoint } = extractMixedMinimumSidePoints(
        minimumResult,
        context.circleSideIsFirst,
    );

    return computeCenterToTargetReferencePoint(
        context.circleSideIsFirst,
        circleCenter,
        nonCirclePoint,
    );
}
